// 权益履约台账 — benefit-fulfillment 台账 + 补推 (carrier-benefit-bundle).
// card-api 履约展示供应商单号 (external_id) 与卡种 (params.card_type);
// failed 单可手动补推 (重置 attempts → 重启 WF_FULFILLMENT).
//
// 后端: handlers/fulfillment/admin.go
//   GET  /admin/v1/fulfillments            (?status=&limit=&cursor=)
//   POST /admin/v1/fulfillments/:id/retry

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Popconfirm, Tag, Typography, message } from 'antd';
import { ResourceList } from '@/components/ResourceList';
import { listFulfillments, retryFulfillment, type Fulfillment } from '@/api/fulfillments';
import { formatDateTime, truncate } from '@/utils/format';

const STATUS_META: Record<Fulfillment['status'], { label: string; color: string }> = {
  pending: { label: '待推送', color: 'gold' },
  pushed: { label: '已推送', color: 'green' },
  confirmed: { label: '已确认', color: 'green' },
  failed: { label: '失败', color: 'red' },
  manual: { label: '人工处理', color: 'default' },
};

const CARD_TYPE_LABEL: Record<string, string> = {
  '1': '单卡卡密',
  '2': '卡号+密码',
  '3': '网址链接',
  '4': '卡密+兑换地址',
};

/** params.card_type (card-api 履约的卡种; 其余 provider 无此字段). */
function cardType(row: Fulfillment): string {
  const ct = row.params?.card_type;
  return ct == null ? '' : String(ct);
}

export function FulfillmentsListPage() {
  // ResourceList 不暴露 reload 给列 render — 用 reloadKey 强制 remount 触发重新拉取
  // (与 skus/cardkeys 页同一惯用法).
  const [reloadKey, setReloadKey] = useState(0);

  const handleRetry = async (id: string) => {
    try {
      await retryFulfillment(id);
      message.success('已重置为待推送并重启履约');
      setReloadKey((k) => k + 1);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '补推失败');
    }
  };

  return (
    <ResourceList<Fulfillment>
      key={reloadKey}
      title="权益履约台账"
      description="支付后 WF_FULFILLMENT 按 provider 发货的履约单. card-api 供应商换卡展示供应商单号与卡种; 失败单可手动补推 (库存/余额恢复后重试即可)."
      rowKey="id"
      pagination="cursor"
      filters={[
        {
          key: 'status',
          label: '状态',
          type: 'select',
          options: [
            { value: 'pending', label: '待推送' },
            { value: 'pushed', label: '已推送' },
            { value: 'confirmed', label: '已确认' },
            { value: 'failed', label: '失败' },
            { value: 'manual', label: '人工处理' },
          ],
        },
      ]}
      fetcher={({ filters, pageSize, cursor }) =>
        listFulfillments({ status: filters.status as string | undefined, cursor, limit: pageSize })
      }
      columns={[
        { title: '履约单', dataIndex: 'id', render: (v: string) => truncate(v, 14) },
        {
          title: '订单',
          dataIndex: 'order_id',
          render: (v: string) => <Link to={`/orders/${v}`}>{truncate(v, 14)}</Link>,
        },
        { title: 'App', dataIndex: 'app_id', render: (v: number) => <Tag color="blue">app#{v}</Tag> },
        { title: 'Provider', dataIndex: 'provider', render: (v: string) => <Tag>{v}</Tag> },
        { title: 'SKU', dataIndex: 'sku_code', render: (v: string) => v || '-' },
        {
          title: '卡种',
          key: 'card_type',
          render: (_, row) => {
            const ct = cardType(row);
            if (!ct) return <Typography.Text type="secondary">-</Typography.Text>;
            return <Tag>{CARD_TYPE_LABEL[ct] ?? `类型${ct}`}</Tag>;
          },
        },
        {
          title: '供应商单号',
          dataIndex: 'external_id',
          render: (v: string | null | undefined) =>
            v ? <Typography.Text copyable>{truncate(v, 20)}</Typography.Text> : <Typography.Text type="secondary">-</Typography.Text>,
        },
        {
          title: '状态',
          dataIndex: 'status',
          render: (v: Fulfillment['status']) => (
            <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.label ?? v}</Tag>
          ),
        },
        { title: '尝试', dataIndex: 'attempts', align: 'right' },
        {
          title: '最后错误',
          dataIndex: 'last_error',
          render: (v: string | null | undefined) =>
            v ? (
              <Typography.Text type="danger" title={v}>
                {truncate(v, 28)}
              </Typography.Text>
            ) : (
              '-'
            ),
        },
        {
          title: '创建时间',
          dataIndex: 'created_at',
          render: (v: string) => formatDateTime(v),
        },
        {
          title: '操作',
          key: 'actions',
          width: 100,
          render: (_, row) => {
            if (row.status !== 'failed') return null;
            return (
              <Popconfirm
                title="补推该履约单?"
                description="重置尝试次数并重启履约 workflow"
                onConfirm={() => handleRetry(row.id)}
                okText="补推"
                cancelText="取消"
              >
                <Button type="link" size="small" style={{ padding: 0 }}>
                  补推
                </Button>
              </Popconfirm>
            );
          },
        },
      ]}
    />
  );
}
